import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { getFirestore, doc, getDoc, setDoc, serverTimestamp } from "firebase/firestore";
import { getStorage, ref as storageRef, uploadBytes, getDownloadURL } from "firebase/storage";
import {
  buildDefaultNextStepsFaqConfig,
  buildResetNextStepsAudioMetadata,
  buildUploadedNextStepsAudioMetadata,
  stripNextStepsFaqSystemFields
} from "../../../helpers/nextStepsFaqHelpers";

const safeStringify = (value, fallback) => {
  try {
    return JSON.stringify(value ?? fallback, null, 2);
  } catch {
    return JSON.stringify(fallback, null, 2);
  }
};

const tryParseJson = (text) => {
  try {
    return { ok: true, value: JSON.parse(text), error: "" };
  } catch {
    return { ok: false, value: null, error: "Invalid JSON. Fix formatting and try again." };
  }
};

const NextStepsFaqTab = ({ isActive, podcastId, selectedLanguage }) => {
  const db = useMemo(() => getFirestore(), []);
  const storage = useMemo(() => getStorage(), []);
  const langCode = String(selectedLanguage || "EN").toUpperCase();
  const canonicalAudioPath = useMemo(() => {
    if (!podcastId || !langCode) return "";
    return `Podcasts/${String(podcastId)}/${langCode}/next-steps.mp3`;
  }, [podcastId, langCode]);
  const defaultConfig = useMemo(() => buildDefaultNextStepsFaqConfig(langCode), [langCode]);
  const docRef = useMemo(() => {
    if (!podcastId || !langCode) return null;
    return doc(db, "podcasts", String(podcastId), "next-steps-faq", langCode);
  }, [db, podcastId, langCode]);

  const [text, setText] = useState(safeStringify(defaultConfig, defaultConfig));
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [audioUrl, setAudioUrl] = useState("");
  const [audioStatus, setAudioStatus] = useState("");
  const [audioError, setAudioError] = useState("");
  const [resettingAudio, setResettingAudio] = useState(false);
  const [status, setStatus] = useState("");
  const [error, setError] = useState("");
  const textAreaRef = useRef(null);

  const parsed = useMemo(() => tryParseJson(text), [text]);

  const resize = () => {
    const el = textAreaRef.current;
    if (!el) return;
    el.style.height = "auto";
    el.style.height = `${Math.min(Math.max(el.scrollHeight, 420), 760)}px`;
  };

  useEffect(() => {
    resize();
  }, [text]);

  const setEditorValue = (updater) => {
    setText((prev) => {
      const parsed = tryParseJson(prev);
      const base = parsed.ok && parsed.value && typeof parsed.value === "object" && !Array.isArray(parsed.value)
        ? parsed.value
        : defaultConfig;
      return safeStringify(updater(base), defaultConfig);
    });
  };

  const refreshAudioPreview = useCallback(async (config) => {
    const meta = config?.audioMetadata || {};
    const objectPath = String(meta.objectPath || "").trim();
    const hasUploadedAudio = meta.hasUploadedAudio === true && objectPath;

    setAudioUrl("");
    setAudioError("");
    if (!hasUploadedAudio) return;

    try {
      const url = await getDownloadURL(storageRef(storage, objectPath));
      setAudioUrl(url);
    } catch (err) {
      console.error("Failed to load next steps audio preview:", err);
      setAudioError("Audio metadata exists, but the file could not be loaded.");
    }
  }, [storage]);

  const load = useCallback(async () => {
    if (!docRef) return;
    try {
      setLoading(true);
      setStatus("");
      setError("");

      const snap = await getDoc(docRef);
      const next = snap.exists() ? stripNextStepsFaqSystemFields(snap.data()) : defaultConfig;
      setText(safeStringify(next, defaultConfig));
      refreshAudioPreview(next);
      setStatus(snap.exists() ? "Loaded." : "Using defaults. Save to create this language config.");
    } catch (err) {
      console.error("Failed to load next steps/FAQ:", err);
      setError("Failed to load.");
    } finally {
      setLoading(false);
    }
  }, [docRef, defaultConfig, refreshAudioPreview]);

  const uploadAudio = async (file) => {
    if (!docRef || !canonicalAudioPath || !file) return;

    try {
      setUploading(true);
      setAudioStatus("");
      setAudioError("");

      const destRef = storageRef(storage, canonicalAudioPath);
      const contentType = file.type || "audio/mpeg";
      await uploadBytes(destRef, file, { contentType });
      const url = await getDownloadURL(destRef);

      const metadata = buildUploadedNextStepsAudioMetadata({
        objectPath: canonicalAudioPath,
        downloadUrl: url,
        contentType,
        fileName: file.name || "next-steps.mp3"
      });

      setEditorValue((base) => ({
        ...base,
        audioMetadata: metadata
      }));

      const parsed = tryParseJson(text);
      const current =
        parsed.ok && parsed.value && typeof parsed.value === "object" && !Array.isArray(parsed.value)
          ? parsed.value
          : defaultConfig;
      const snap = await getDoc(docRef);
      const timestampFields = {
        createdAt: snap.exists() ? snap.data()?.createdAt || serverTimestamp() : serverTimestamp(),
        updatedAt: serverTimestamp()
      };

      await setDoc(docRef, { ...current, audioMetadata: metadata, ...timestampFields });
      setAudioUrl(url);
      setAudioStatus("Uploaded and saved.");
      setStatus("Saved.");
    } catch (err) {
      console.error("Failed to upload next steps audio:", err);
      setAudioError("Failed to upload audio.");
    } finally {
      setUploading(false);
    }
  };

  const resetAudio = async () => {
    if (!docRef) return;

    const resetMetadata = buildResetNextStepsAudioMetadata();

    try {
      setResettingAudio(true);
      setAudioStatus("");
      setAudioError("");

      setEditorValue((base) => ({
        ...base,
        audioMetadata: resetMetadata
      }));

      const parsed = tryParseJson(text);
      const current =
        parsed.ok && parsed.value && typeof parsed.value === "object" && !Array.isArray(parsed.value)
          ? parsed.value
          : defaultConfig;
      const snap = await getDoc(docRef);
      const timestampFields = {
        createdAt: snap.exists() ? snap.data()?.createdAt || serverTimestamp() : serverTimestamp(),
        updatedAt: serverTimestamp()
      };

      await setDoc(docRef, { ...current, audioMetadata: resetMetadata, ...timestampFields });
      setAudioUrl("");
      setAudioStatus("Audio reset. Default audio will be used.");
      setStatus("Saved.");
    } catch (err) {
      console.error("Failed to reset next steps audio:", err);
      setAudioError("Failed to reset audio.");
    } finally {
      setResettingAudio(false);
    }
  };

  const save = async () => {
    if (!docRef) return;
    const p = tryParseJson(text);
    if (!p.ok) {
      setError(p.error);
      return;
    }
    if (!p.value || typeof p.value !== "object" || Array.isArray(p.value)) {
      setError("JSON must be an object at the top level.");
      return;
    }

    try {
      setSaving(true);
      setStatus("");
      setError("");

      const snap = await getDoc(docRef);
      const timestampFields = {
        createdAt: snap.exists() ? snap.data()?.createdAt || serverTimestamp() : serverTimestamp(),
        updatedAt: serverTimestamp()
      };

      await setDoc(docRef, { ...p.value, ...timestampFields });
      setStatus("Saved.");
    } catch (err) {
      console.error("Failed to save next steps/FAQ:", err);
      setError("Failed to save.");
    } finally {
      setSaving(false);
    }
  };

  useEffect(() => {
    if (!isActive) return;
    load();
  }, [isActive, docRef?.path, load]);

  if (!isActive) return null;

  return (
    <div style={{ display: "grid", gap: 14 }}>
      <div style={{ background: "#fff", border: "1px solid #e5e7eb", borderRadius: 12, padding: 12 }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12 }}>
          <div>
            <div style={{ fontWeight: 800, fontSize: 16 }}>Next steps and FAQs ({langCode})</div>
          </div>
          <div style={{ display: "flex", gap: 8 }}>
            <button onClick={load} disabled={loading || !docRef}>
              {loading ? "Loading..." : "Reload"}
            </button>
            <button onClick={save} disabled={saving || !docRef || !parsed.ok}>
              {saving ? "Saving..." : "Save"}
            </button>
          </div>
        </div>



        <textarea
          ref={textAreaRef}
          value={text}
          onChange={(e) => setText(e.target.value)}
          spellCheck={false}
          style={{
            width: "100%",
            minHeight: 420,
            marginTop: 12,
            overflowY: "auto",
            resize: "vertical",
            padding: 10,
            borderRadius: 8,
            border: "1px solid #ddd",
            fontFamily: "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace",
            whiteSpace: "pre",
            lineHeight: 1.35
          }}
        />

<div
          style={{
            marginTop: 12,
            padding: 12,
            border: "1px solid #e5e7eb",
            borderRadius: 10,
            background: "#f9fafb",
            display: "grid",
            gap: 10
          }}
        >
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12 }}>
            <label
              style={{
                display: "inline-flex",
                alignItems: "center",
                padding: "8px 12px",
                borderRadius: 8,
                background: uploading ? "#9ca3af" : "#2563eb",
                color: "#fff",
                fontWeight: 700,
                cursor: uploading || !docRef ? "default" : "pointer"
              }}
            >
              {uploading ? "Uploading..." : "Upload MP3"}
                <input
                type="file"
                accept="audio/mpeg,audio/mp3"
                disabled={uploading || !docRef}
                onChange={(e) => {
                  const file = e.target.files && e.target.files[0];
                  if (file) uploadAudio(file);
                  e.target.value = "";
                }}
                style={{ display: "none" }}
              />
            </label>
            <button
              type="button"
              onClick={resetAudio}
              disabled={resettingAudio || uploading || !docRef}
              style={{
                padding: "8px 12px",
                borderRadius: 8,
                border: "1px solid #d1d5db",
                background: resettingAudio || uploading || !docRef ? "#e5e7eb" : "#fff",
                color: "#374151",
                fontWeight: 700,
                cursor: resettingAudio || uploading || !docRef ? "default" : "pointer"
              }}
            >
              {resettingAudio ? "Resetting..." : "Reset audio"}
            </button>
          </div>

          {audioUrl && <audio controls src={audioUrl} style={{ width: "100%" }} />}
          {audioStatus && <div style={{ color: "#065f46", fontWeight: 700 }}>{audioStatus}</div>}
          {audioError && <div style={{ color: "crimson", fontWeight: 700 }}>{audioError}</div>}
        </div>

        <div style={{ marginTop: 8, display: "flex", gap: 10, alignItems: "center" }}>
          {error && <div style={{ color: "crimson", fontWeight: 700 }}>{error}</div>}
          {!error && !parsed.ok && <div style={{ color: "crimson", fontWeight: 700 }}>{parsed.error}</div>}
          {!error && parsed.ok && status && <div style={{ color: "#065f46", fontWeight: 700 }}>{status}</div>}
        </div>
      </div>
    </div>
  );
};

export default NextStepsFaqTab;
